import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { ChevronDown, ChevronRight, FileDown, Loader2, RefreshCw } from 'lucide-react';
import { openInvoicePdf } from './billingApi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => `€${(Number(v) || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt = (d) => (d ? d.split('-').reverse().join('/') : '—');

const DebtorRow = ({ d }) => {
  const [open, setOpen] = useState(false);
  const key = d.client_nif || d.client_name || 'sin-cliente';
  const late = (d.oldest_days ?? 0) > 30;
  return (
    <div className="border-b last:border-b-0" data-testid={`debtor-${key}`}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left">
        {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
        <div className="flex-1 min-w-[180px]">
          <p className="font-semibold text-slate-800">{d.client_name || '(sin cliente)'}</p>
          <p className="text-xs text-slate-500">{d.client_nif || 'Sin NIF'}</p>
        </div>
        <span className="text-sm text-slate-600 w-28 text-right">{d.count} {d.count === 1 ? 'factura' : 'facturas'}</span>
        <span className="text-sm font-bold text-amber-700 w-28 text-right" data-testid={`debtor-total-${key}`}>{eur(d.pending_total)}</span>
        <span className={`text-xs w-40 text-right ${late ? 'text-rose-600 font-semibold' : 'text-slate-500'}`}>
          Más antigua: {fmt(d.oldest_date)}{d.oldest_days !== null ? ` (${d.oldest_days} d)` : ''}
        </span>
      </button>
      {open && (
        <div className="bg-slate-50/60 px-4 pb-3 space-y-1">
          {d.invoices.map((i) => (
            <div key={i.id} className="flex items-center justify-between text-xs border-t border-slate-200 pt-1.5">
              <span><b>{i.invoice_number || '(sin número)'}</b> · {fmt(i.invoice_date)} · {i.status}</span>
              <span className="flex items-center gap-2">
                <b>{eur(i.total)}</b>
                <Button variant="ghost" size="sm" className="h-6 px-2" onClick={() => openInvoicePdf(i.id, `${i.invoice_number}.pdf`)} data-testid={`debtor-pdf-${i.invoice_number}`}>
                  <FileDown className="h-3.5 w-3.5" />
                </Button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const DebtorsPanel = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const load = async () => {
    setLoading(true);
    try { setData((await axios.get(`${API}/admin/income-invoices/debtors`)).data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [refreshKey]);

  return (
    <Card className="overflow-hidden border-amber-200" data-testid="debtors-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-amber-50 border-b border-amber-200">
        <button type="button" onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-2 text-left" data-testid="debtors-collapse">
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          <div>
            <p className="font-bold text-amber-900">Deudores</p>
            <p className="text-xs text-amber-700">Facturas emitidas pendientes de cobro, por cliente</p>
          </div>
        </button>
        <div className="flex items-center gap-4">
          {data && (
            <p className="text-sm text-amber-900" data-testid="debtors-summary">
              <b>{eur(data.total_pending)}</b> pendiente · {data.total_invoices} facturas · {data.debtors.length} clientes
            </p>
          )}
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="debtors-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      {!collapsed && (
        <div className="max-h-[420px] overflow-y-auto">
          {data?.debtors.length === 0 && <p className="text-sm text-slate-500 p-4">Ningún cliente te debe dinero ahora mismo.</p>}
          {data?.debtors.map((d) => <DebtorRow key={d.client_nif || d.client_name} d={d} />)}
        </div>
      )}
    </Card>
  );
};

export default DebtorsPanel;
